import {
  Component,
  ElementRef,
  OnInit,
  AfterViewInit,
  inject,
  model,
  signal,
  viewChild,
  computed
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { forkJoin, map, of, switchMap } from 'rxjs';

import * as Sentry from '@sentry/angular';

import {
  faBan,
  faCheckCircle,
  faCircleExclamation,
  faDollarSign,
  faPrint,
  faRotateLeft,
  faSpinner,
  faUserGraduate,
  faUserPen,
  faUserPlus,
  faUserXmark,
  faCircleXmark,
  faFileExcel,
  faUsersBetweenLines,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';

import { StudentsService } from '../../core/services/students.service';
import { UtilityService } from '../../core/services/utility.service';
import { SchoolAdminTeacherFilterService } from '../../shared/components/school-admin-teacher-filter/school-admin-teacher-filter.service';

import { Student } from '../../core/models/student.model';
import { LoginService } from '../../core/services/login.service';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { ManagementUser } from '../../core/models/management-user.model';
import { DialogService } from 'src/app/core/services/dialog.service';
import { PrintService } from 'src/app/core/services/print.service';

import introJs from 'intro.js';
import { studentsListIntroSteps } from './students-list.intro-steps';
import { canViewCoTeacherFilter } from 'src/app/reports/utils';
import { StudentsFilterComponent } from 'src/app/shared/components/students-filter/students-filter.component';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';
import { BulkStudentEditPayload } from 'src/app/core/models/student.model';
import { EnrollmentService } from '../../core/services/enrollment.service';

@Component({
    selector: 'wf-students-list',
    templateUrl: './students-list.component.html',
    styleUrls: ['./students-list.component.css'],
    standalone: false
})
export class StudentsListComponent implements OnInit, AfterViewInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly studentService = inject(StudentsService);
  private readonly enrollmentService = inject(EnrollmentService);
  private readonly utilityService = inject(UtilityService);
  private readonly teacherFilterService = inject(SchoolAdminTeacherFilterService);
  private readonly loginService = inject(LoginService);
  private readonly sessionService = inject(SessionStorageService);
  private readonly dialogService = inject(DialogService);
  private readonly printService = inject(PrintService);
  readonly studentFilter = viewChild.required<StudentsFilterComponent>(StudentsFilterComponent);
  readonly printContent = viewChild.required<ElementRef<HTMLElement>>('printContent');
  showCancelDialog: boolean = false;
  showPurchaseDialog: boolean = false;
  showSectionAddMessage: boolean = false;
  sectionAddMessage: any = {};

  hideSharedStudents = model(false);
  readonly managementUser = signal<ManagementUser | null>(null);
  readonly currentFilters = signal<any>(null);
  readonly teacherFilter = signal<number>(0);
  readonly isDeleting = signal(false);
  readonly isExporting = signal(false);
  readonly isBulkEditSaving = signal(false);
  readonly saveBulkEditError = signal('');
  readonly message = signal('');
  readonly messageTitle = signal('');
  readonly messageStatus = signal('');
  readonly isCleverTeacher = signal(false);
  readonly isClasslinkTeacher = signal(false);
  readonly students = signal<Student[]>([]);
  readonly selectedStudents = signal<Set<number>>(new Set<number>());
  readonly teacherNamesById = signal<Record<number, string>>({});
  readonly showBulkEdit = signal<boolean>(false);
  readonly teachers = signal<ManagementUser[]>([]);

  readonly userRole = computed(() => {
    return this.managementUser()?.userRole ?? '';
  });
  readonly isParentUser = computed(() => {
    return this.managementUser()?.isParentUser() ?? false;
  });
  readonly isReviewTeacher = computed(() => {
    return this.managementUser()?.isTeacherReviewUser() ?? false;
  });
  readonly showTeacherFilter = computed(() => {
    return this.managementUser()?.isSchoolAdmin() ?? false;
  });
  readonly showCoTeacherFilter = computed(() => {
    // hide the shared students toggle for would-be valid users that don't have students shared with them.
    const user = this.managementUser();
    const students = this.students();

    let access = canViewCoTeacherFilter(this.managementUser());
    if (access && user?.isTeacherUser()) {
      access = students.some(s => s.fromCoTeacherAccess);
    }

    return access;
  });
  readonly canPurchase = computed(() => {
    return this.isParentUser() && this.route.snapshot.data['resolveData'].licenseCount === 0;
  });
  readonly canManuallyEnroll = computed(() => {
    // In general, teachers and School Admins that do not use SSO logins can manually enroll (add) students
    //
    // You can manually enroll a student if:
    //   1. You do not belong to an SSO site (a school that is using SSO/external IdP for their user logins)
    //   2. You are a parent user with unused purchased licenses (not relevant currently)
    //   3. You are not a teacher review user
    return !Boolean(this.managementUser()?.belongsToSSOSchool()) &&
      !this.isCleverTeacher() &&
      !this.isClasslinkTeacher() &&
      !this.canPurchase() &&
      !Boolean(this.managementUser()?.isTeacherReviewUser())
  });
  readonly canBulkEdit = computed(() => {
    return this.managementUser()?.isSchoolAdmin() ?? false;
  })
  readonly mainFilteredStudents = computed(() => {
    const allStudents = this.students();
    const filters = this.currentFilters();
    // Use this as a flag as a dependency so that whenever the teacher filter is changed by a School Admin user,
    // this computed signal is updated and the displayed students are re-filtered. We don't care about the value,
    // this is a bit of a smell and can be refactored when we refactor the SchoolAdminTeacherFilterComponent to use signals
    this.teacherFilter();

    // grade and class filters
    let filtered = this.studentService.filterStudents(allStudents, filters || {});

    // any teacher filters (really only matters for school admins), non school admins will return passed students
    return this.teacherFilterService.filterStudentsBySelectedTeacher(filtered);
  });
  readonly displayedStudents = computed(() => {
    const filtered = this.mainFilteredStudents();
    const hideShared = this.hideSharedStudents();
    const coTeacherAccess = this.showCoTeacherFilter();

    if (hideShared && coTeacherAccess) {
      return filtered.filter(s => !s.fromCoTeacherAccess);
    }

    return filtered;
  });
  readonly studentTeachers = computed(() => {
    return new Map<number, number>(this.students().map(s => [s.userID, s.teacherID]));
  })
  readonly studentsSelected = computed(() => {
    return this.selectedStudents().size > 0;
  });
  readonly sharedTeacherId = computed(() => {
    const selected = this.selectedStudents();
    const studentTeachers = this.studentTeachers();

    if (selected.size === 0) return null;

    // Find our teacher IDs for the selected students in a performant way
    const [firstStudentId] = selected;
    const targetTeacherId = studentTeachers.get(firstStudentId);
    if (!targetTeacherId) return null;

    for (const id of selected) {
      if (studentTeachers.get(id) !== targetTeacherId) {
        return null;
      }
    }

    return targetTeacherId;
  });
  readonly classes = computed(() => {
    const user = this.managementUser();
    const allStudents = this.students();

    // Again, use this as a flag as a dependency so that whenever the teacher filter is changed by a School Admin user,
    // Refactor when we refactor the SchoolAdminTeacherFilterComponent to use signals
    this.teacherFilter();

    if (!allStudents || allStudents.length === 0) {
      return [{
        name: 'All',
        schoolClassID: 0,
        teacherID: 0,
        createDate: (new Date()),
        status: ''
      }];
    }

    // Get all classes and sort them, if we are not a school admin, these are our final classes
    if (!user?.isSchoolAdmin()) {
      return this.utilityService.sortClasses(this.route.snapshot.data['resolveData'].classes);
    }

    // School admin users get filtered classes based on the selected teacher
    const teacherStudents = this.teacherFilterService.filterStudentsBySelectedTeacher(allStudents);
    const uniqueClasses = this.utilityService.getUniqueSchoolClasses(teacherStudents);
    return [{
      name: 'All',
      schoolClassID: 0,
      teacherID: 0,
      createDate: (new Date()),
      status: ''
    }, ...this.utilityService.sortClasses(uniqueClasses)];
  });
  readonly teacherClasses = computed(() => {
    const classMap = new Map<number, string[]>();

    for (const schoolClass of this.classes()) {
      if (schoolClass.schoolClassID === 0) continue;

      const classNames = classMap.get(schoolClass.teacherID) ?? [];
      classNames.push(schoolClass.name);
      classMap.set(schoolClass.teacherID, classNames);
    }

    return classMap;
  });

  readonly grades = computed(() => {
    const user = this.managementUser();
    const displayedStudents = this.displayedStudents();

    // Again, use this as a flag as a dependency so that whenever the teacher filter is changed by a School Admin user,
    // Refactor when we refactor the SchoolAdminTeacherFilterComponent to use signals
    this.teacherFilter();

    if (!displayedStudents || displayedStudents.length === 0) {
      return ['All'];
    }

    if (!user?.isSchoolAdmin()) {
      // If we aren't a school admin, our grades are just what came in
      return this.utilityService.sortGrades(this.route.snapshot.data['resolveData'].grades);
    }

    const grades = [...new Set(displayedStudents.map(s =>
      this.utilityService.getStudentGrade(s.grade)
    ))];

    return ['All', ...this.utilityService.sortGrades(grades)];
  }, {
    equal: (a, b) => JSON.stringify(a) === JSON.stringify(b)
  });

  // Icons
  enrollIcon = CATEGORY_ICONS['enroll'];
  printIcon = faPrint;
  excelIcon = faFileExcel;
  studentIcon = faUserGraduate;
  addStudentIcon = faUserPlus;
  deleteStudentIcon = faUserXmark;
  editStudentIcon = faUserPen;
  loadingIcon = faSpinner;
  successIcon = faCheckCircle;
  errorIcon = faCircleExclamation;
  purchaseIcon = faDollarSign;
  cancelIcon = faBan;
  returnIcon = faRotateLeft;
  closeIcon = faCircleXmark;
  coTeacherIcon = faUsersBetweenLines;
  helpIcon = faQuestionCircle;
  introTour: any;

  ngOnInit(): void {
    this.managementUser.set(this.sessionService.userData());
    this.students.set(this.route.snapshot.data['resolveData'].students);
    this.isCleverTeacher.set((this.sessionService.userData()?.isTeacherUser() ?? false) && this.sessionService.getSSOInfo()?.provider === 'clever');
    this.isClasslinkTeacher.set((this.sessionService.userData()?.isTeacherUser() ?? false) && this.sessionService.getSSOInfo()?.provider === 'classlink');
    this.teacherNamesById.set(this.teacherFilterService.getTeacherNamesById());
    this.teachers.set(this.sessionService.getSchoolAdminTeacherFilter()?.teachers ?? []);

    this.sectionAddMessage = this.dialogService.getSectionAddMessage();
    if (this.sectionAddMessage.showMessage) {
      this.showSectionAddMessage = true;
    }
  }

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: studentsListIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showBullets: true,
      showSkip: false,
    });
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }
      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(studentsListIntroSteps);
    this.introTour.setOptions({
      steps: filteredSteps
    });
    this.introTour.start();
  }

  studentToggled(e: any) {
    const input = e.target as HTMLInputElement;
    const id = Number(input.value);

    this.selectedStudents.update((prev) => {
      const selected = new Set(prev);
      if (input.checked) {
        selected.add(id);
      } else {
        selected.delete(id);
      }

      return selected;
    })
  }

  bulkEditAllToggled(e: any) {
    const input = e.target as HTMLInputElement;
    const checked = input.checked;
    this.selectedStudents.set(checked ? new Set(this.displayedStudents().map(s => s.userID)) : new Set<number>());
  }

  filterStudents(filterOpts: any) {
    this.currentFilters.set(filterOpts);
    this.selectedStudents.set(new Set<number>());
  }

  onTeacherFilterChange() {
    this.teacherFilter.update(s => s +1);
    this.selectedStudents.set(new Set<number>());
    this.studentFilter().resetFilterSettings();
  }

  archiveStudents() {
    this.isDeleting.set(true);
    this.studentService.archiveStudents(Array.from(this.selectedStudents())).pipe(
      switchMap(() => {
        return this.studentService.getStudents(this.managementUser()?.isSchoolAdmin() ? 'school' : 'mine')
      })
    ).subscribe({
      next: (students: Student[]) => {
        const deletedCount = this.selectedStudents().size;
        this.students.set(students);
        this.selectedStudents.set(new Set<number>());
        this.isDeleting.set(false);

        this.messageTitle.set('Student Deletion');
        this.messageStatus.set('success');
        this.message.set(`${deletedCount} student${deletedCount > 1 ? 's were' : ' was'} successfully deleted`);
      },
      error: (err: any) => {
        this.isDeleting.set(false);
        this.messageTitle.set('Student Deletion');
        this.messageStatus.set('error');
        this.message.set(`There was a problem deleting the selected Students. Please try again or contact us for help.`);

        Sentry.captureException(err, {
          tags: {
            section: 'student-enrollment',
            action: 'delete',
          }
        });
      },
    });
  }

  cancelStudentSubscriptions() {
    this.studentService.cancelStudentSubscriptions(Array.from(this.selectedStudents())).pipe(
      map((cancelStatuses) => {
        return cancelStatuses.filter((status: number) => status !== 200).length;
      }),
      switchMap((failedCount: any) => {
        this.messageTitle.set('Subscription Cancellation');
        if (failedCount > 0) {
          this.messageStatus.set('error');
          this.message.set(`There was a problem cancelling your ${failedCount} ${failedCount > 1 ? 'subscriptions' : 'subscription'}`);
        } else {
          this.messageStatus.set('success');
          this.message.set(`${this.selectedStudents().size} subscription${this.selectedStudents().size > 1 ? 's were' : ' was'} successfully cancelled`);
        }
        return this.studentService.isParentEnabled(this.sessionService.userData()?.userID);
      }),
      switchMap((parentEnabled: boolean) => {
        if (!parentEnabled) {
          this.loginService.logout();
          return of([]);
        } else {
          return this.studentService.getStudents();
        }
      })
    ).subscribe({
      next: (students) => {
        if (students.length > 0) {
          this.students.set(students);
        }
        this.selectedStudents.set(new Set<number>());
        this.showCancelDialog = false;
      }
    });
  }

  saveBulkEdit(data: BulkStudentEditPayload) {
    this.isBulkEditSaving.set(true);
    this.enrollmentService.updateStudentsInfo(data).pipe(
      switchMap(() => {
        return this.studentService.getStudents(this.managementUser()?.isSchoolAdmin() ? 'school' : 'mine');
      })
    ).subscribe({
      next: (updatedStudents: Student[]) => {
        // If we successfully saved, (1) show a message, (2) close the dialog, (3) refresh the students list
        this.messageTitle.set('Bulk Student Edit');
        this.messageStatus.set('success');
        this.message.set('The selected Students were successfully updated');
        this.isBulkEditSaving.set(false);
        this.showBulkEdit.set(false);
        this.saveBulkEditError.set('');
        this.students.set(updatedStudents);
      },
      error: (err: any) => {
        // On error, we want to have the dialog stay open and display an error message there so the user can try again
        this.isBulkEditSaving.set(false);
        this.saveBulkEditError.set(err.error?.message ?? 'Check the selected properties');
      }
    });
  }

  addStudent() {
    this.router.navigateByUrl('student/add');
  }

  editStudent(studentId: number) {
    this.router.navigateByUrl(`student/edit/${studentId}`);
  }

  goToAddCleverSections() {
    this.router.navigateByUrl('clever/roster');
  }

  goToAddClassLinkSections() {
    this.router.navigateByUrl('classlink/roster');
  }

  closeSectionAddNotification() {
    this.showSectionAddMessage = false;
    this.dialogService.clearSectionAddMessage();
  }

  showBulkEditDialog() {
    this.message.set('');
    this.saveBulkEditError.set('');
    this.showBulkEdit.set(true);
  }

  closeBulkEditDialog() {
    this.showBulkEdit.set(false);
  }

  printStudents() {
    const userData = this.sessionService.userData();
    let teacher = `${userData?.firstName} ${userData?.lastName}`;
    if (this.showTeacherFilter()) {
      const selectedTeacherName = this.teacherFilterService.getSelectedTeacherName();
      if (selectedTeacherName != null) {
        teacher = selectedTeacherName;
      }
    }

    let filterText = '';
    const filterParts = [];
    const filters = this.currentFilters();

    if (filters) {
      if (filters.grade && filters.grade !== 'all') {
        let gradeDisplay = '';
        if (filters.grade === 'four_below_all') {
          gradeDisplay = 'All 4th & Below';
        } else if (filters.grade === 'five_up_all') {
          gradeDisplay = 'All 5th & Up';
        } else {
          gradeDisplay = `Grade ${filters.grade}`;
        }
        filterParts.push(`Grade: ${gradeDisplay}`);
      }

      if (filters.class && filters.class !== 0) {
        const selectedClass = this.classes().find(c => c.schoolClassID === filters.class);
        if (selectedClass && selectedClass.name !== 'All') {
          filterParts.push(`Class: ${selectedClass.name}`);
        }
      }
    }

    if (filterParts.length > 0) {
      filterText = filterParts.join(' | ');
    }

    this.printService.openPrintWindow(
      this.printContent().nativeElement,
      userData?.school || '',
      teacher,
      filterText
    );
  }

  exportStudentList() {
    this.isExporting.set(true);
    this.studentService.exportStudentList(this.displayedStudents().map(student => student.userID)).subscribe({
      next: (blob: Blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = 'student_list.xlsx';
        a.click();
        URL.revokeObjectURL(objectUrl);
        this.isExporting.set(false);
      },
      error: (err: any) => {
        this.isExporting.set(false);
        this.messageTitle.set('Student List Export');
        this.messageStatus.set('error');
        this.message.set('There was an error trying to export the Student List. Please contact support for assistance.');

        Sentry.captureException(err, {
          tags: {
            section: 'student-enrollment',
            action: 'export',
          }
        });
      }
    });
  }
}
